import { Component } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { map, findIndex, get, size } from "lodash"
import { CardContent, Paper } from "@mui/material"
import { PERIOD, DATE } from "helpers/shapes"
import { computeCSVData } from "helpers/csv"
import { smallPercentageFormatter } from "helpers/formatters/numbers"
import FactorTrendsPerformanceDetails from "modules/factor-trends-performance-details"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import NoData from "components/no-data"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import "./style.scss"

export default class FactorTrendsPerformance extends Component {
  static propTypes = {
    account: PropTypes.string.isRequired,
    earliestDate: DATE,
    latestDate: DATE,
    startDate: DATE,
    endDate: DATE,
    firstDate: DATE,
    selectedDate: DATE,
    period: PERIOD.isRequired,
    data: PropTypes.array.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      disabled: PropTypes.bool.isRequired,
    })).isRequired,
    factorCategory: PropTypes.string.isRequired,
    setDate: PropTypes.func.isRequired,
    setFactorCategory: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    trackCSVDownload: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    resetDate: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })).isRequired,
    fileName: PropTypes.string.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    only: PropTypes.string,
    datesAdjusted: PropTypes.bool.isRequired,
    selectedViews: PropTypes.array,
    selectedOption: PropTypes.object.isRequired,
    displayAddView: PropTypes.bool.isRequired,
    modelId: PropTypes.string.isRequired,
    addSelectedView: PropTypes.func.isRequired,
    openDialog: PropTypes.func.isRequired,
    availableSelectViewFactors: PropTypes.array.isRequired,
    removeSelectedView: PropTypes.func.isRequired,
    driftArrows: PropTypes.object,
  }

  componentDidUpdate() {
    const { firstDate, startDate: propsStartDate, selectedDate } = this.props
    const startDate = firstDate || propsStartDate

    if (selectedDate && (selectedDate < startDate)) {
      this.props.resetDate()
    }
  }

  onTabChanged = (_, id) => {
    const { setFactorCategory } = this.props
    setFactorCategory(id)
  }

  addSelectedView = (factor) => {
    const { addSelectedView, setFactorCategory } = this.props
    addSelectedView(factor.id)
    setFactorCategory(factor.id)
  }

  handleRemoveSelectedView = (factorId) => {
    const { removeSelectedView, factorCategory, setFactorCategory, options } = this.props
    if (factorCategory === factorId) {
      const optionIndex = findIndex(options, option => option.id === factorId)
      setFactorCategory(get(options, `${optionIndex - 1}.id`))
    }
    removeSelectedView(factorId)
  }

  render() {
    const {
      earliestDate,
      latestDate,
      setDate,
      selectedDate,
      startDate,
      endDate,
      data,
      items,
      factorCategory,
      options,
      toggle,
      focusChartLine,
      blurChartLine,
      focusedChartLine,
      fileName,
      selectedOption,
      trackCSVDownload,
      toggleOnly,
      only,
      selectedViews,
      displayAddView,
      openDialog,
      availableSelectViewFactors,
      driftArrows,
    } = this.props

    let content
    if (!earliestDate || !latestDate || !size(data)) {
      content = <NoData title="Performance Trends" className="graph" />
    } else {
      content = <CardContent className={classnames("card-content")}>
        <Paper className="content">
          <div className="graph">
            <GraphTopBar {...{
              displayAddView,
              openDialog,
              addView: this.addSelectedView,
              value: factorCategory,
              factors: availableSelectViewFactors,
              onTabChanged: this.onTabChanged,
              tabs: map(options, ({ id, label, isSelectedView }) => ({
                key: id,
                value: id,
                removable: isSelectedView,
                onRemoveTab: () => this.handleRemoveSelectedView(id),
                label,
              })),
              actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackCSVDownload} />],
            }}
            />
            <MultiLine
              tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={(v) => v === "--" ? v : smallPercentageFormatter(v)} />}
              {...{
                tickFormat: smallPercentageFormatter,
                startDate,
                date: selectedDate,
                setDate,
                endDate,
                data,
                onlyItem: only,
                items,
                toggle,
                focusChartLine,
                blurChartLine,
                focusedChartLine,
                toggleOnly: (factorId) => toggleOnly(factorId, only !== factorId, items),
              }}
            />
          </div>
          <FactorTrendsPerformanceDetails
            startDate={startDate}
            selectedDate={selectedDate}
            selectedOption={selectedOption}
            selectedViews={selectedViews}
            details={data}
            driftArrows={driftArrows}
          />
        </Paper>
      </CardContent>
    }

    return <div className="modules_factor-trends-performance">
      {content}
    </div>
  }
}
